// DiscoveryRecommendation — Sprint 3 of the Member Experience IA Correction.
// Renders after Path A's Discovery module completes. Consumes the session
// (session.discoveryRecommendations or session.recommendations) and surfaces:
//   • Primary recommendation card (large, peach)
//   • Alternative programs ("worth considering")
//   • Open consultation block (sage)
//   • Sticky bottom selection bar
//
// Reconciliation per spec §10:
//   • A program already in the application tray (selected, awaiting payment)
//     shows "✓ ALREADY IN YOUR PLAN" and is non-interactive (its $X fee is
//     already counted by the tray's normal pricing).
//   • A program already covered by an active subscription shows
//     "✓ ALREADY ACTIVE — view subscription" and links to /app/plan.
//
// CMS-ready: when the backend ships per-recommendation rationale strings
// (whyThisFits, whyAlsoRelevant) we surface them; otherwise fall back to
// short program-meta tagline.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import Logo from "@/components/Logo";
import { Spinner } from "@/components/Loader";
import { apiGet, apiPost } from "@/lib/api";
import { useApplicationTray } from "@/lib/applicationTray";
import { C, colorSchemeForProgram, metaForSlug } from "@/lib/brand";
import { fmtMoney } from "@/lib/format";
import { toast } from "@/components/Toast";

export default function DiscoveryRecommendation() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [subs, setSubs] = useState([]);
  const [error, setError] = useState(null);
  const [continuing, setContinuing] = useState(false);
  const { items: trayItems, addOrReplace } = useApplicationTray();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    Promise.all([
      apiGet(`/consumer/assessment/sessions/${sessionId}`).catch((e) => { setError(e); return null; }),
      apiGet("/consumer/subscriptions").catch(() => []),
    ]).then(([s, sb]) => {
      if (cancelled) return;
      setSession(s);
      setSubs(Array.isArray(sb) ? sb : []);
    });
    return () => { cancelled = true; };
  }, [sessionId]);

  const recommendations = useMemo(() => extractRecommendations(session), [session]);
  const primary = recommendations[0] || null;
  const alternatives = recommendations.slice(1);

  // Selections made on this screen — start with primary auto-selected per
  // spec §7 ("ADD TO MY PLAN" is the primary CTA). We do this in a memo
  // tied to session id so refresh restores cleanly without overwriting user
  // intent if they removed it.
  const [chosen, setChosen] = useState(new Set());
  useEffect(() => {
    if (primary?.slug) setChosen(new Set([primary.slug]));
  }, [primary?.slug]);

  // Reconciliation per spec §10. Hoisted above any early returns so the
  // hook ordering stays stable across renders (rules-of-hooks).
  const reconcile = (slug) => {
    const inPlan = trayItems.some((it) => it.programSlug === slug);
    const activeSubs = subs.filter((s) => ["active", "paused", "cancel_at_period_end"].includes(s.status));
    const isActive = activeSubs.some((s) => s.programSlug === slug);
    return { inPlan, isActive };
  };

  // Programs that will be added to the tray on continue (excluding any
  // already in tray or already active).
  const newlySelected = useMemo(
    () => recommendations.filter((r) => {
      if (!chosen.has(r.slug)) return false;
      const inPlan = trayItems.some((it) => it.programSlug === r.slug);
      const activeSubs = subs.filter((s) => ["active", "paused", "cancel_at_period_end"].includes(s.status));
      const isActive = activeSubs.some((s) => s.programSlug === r.slug);
      return !inPlan && !isActive;
    }),
    [recommendations, chosen, trayItems, subs],
  );

  if (error) {
    return (
      <ErrorScreen onHome={() => navigate("/app")} />
    );
  }
  if (!session) {
    return <div style={{ padding: 64, textAlign: "center", background: C.cream, minHeight: "100vh" }}><Spinner /></div>;
  }

  const toggle = (rec) => {
    const slug = rec.slug;
    const { inPlan, isActive } = reconcile(slug);
    if (inPlan || isActive) return; // non-interactive
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const totalSelected = newlySelected.length + trayItems.filter((it) => !subs.some((s) => s.programSlug === it.programSlug && ["active", "paused"].includes(s.status))).length;
  const reviewFee = totalSelected === 0 ? 0 : 4900 + (totalSelected - 1) * 1900;

  const onContinue = async () => {
    if (continuing) return;
    setContinuing(true);
    try {
      // Add all newly chosen programs to the tray. Path A and Path B
      // converge here — the tray is the single source of selection truth.
      for (const r of newlySelected) {
        addOrReplace({
          programId: r.programId || r.id || r.slug,
          programSlug: r.slug,
          programName: r.name,
          brandColor: r.color,
          tierId: r.recommendedTierId || null,
          tierKey: r.recommendedTierKey || null,
          tierName: r.recommendedTierName || "Recommended",
          monthlyPrice: r.monthlyPrice || 0,
        });
      }
      // Tell the backend which programs the member chose so it can mount
      // their per-program assessment modules. If the endpoint isn't yet
      // implemented we silently fall through to the welcome screen — the
      // tray is still source-of-truth for the next state.
      try {
        await apiPost(`/consumer/assessment/sessions/${sessionId}/select-programs`, {
          selectedProgramIds: newlySelected.map((r) => r.programId || r.id || r.slug),
          selectedProgramSlugs: newlySelected.map((r) => r.slug),
        });
      } catch (_) { /* backend may not have shipped this endpoint yet */ }
      // Sprint 4: hand off to Stripe checkout for the review fee, then
      // /welcome on success. Until backend wires the actual payment step
      // we still route through /checkout so the orchestration matches
      // spec §3 Path A.
      navigate(`/app/checkout/${sessionId}`);
    } catch (err) {
      toast(err.detail || "Couldn't save your choices. Try again in a moment.", "error");
    } finally {
      setContinuing(false);
    }
  };

  const noRecommendations = recommendations.length === 0;

  return (
    <div data-testid="discovery-recommendation" style={{ minHeight: "100vh", background: C.cream, paddingBottom: 140 }}>
      <header style={{ background: C.ink, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Logo light size={20} />
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.peach }}>★ DISCOVERY · COMPLETE</span>
      </header>

      <section style={{ padding: "56px 22px 24px", maxWidth: 900, margin: "0 auto" }}>
        <Eyebrow color={C.peachDeep}>★ BASED ON WHAT YOU SHARED</Eyebrow>
        {primary ? (
          <Headline
            before="WE THINK"
            italic={primary.name?.toLowerCase()}
            after="is the right place to start."
            size={56}
            className="mt-4"
          />
        ) : (
          <Headline before="A PHYSICIAN" italic="might serve you better." size={56} className="mt-4" />
        )}
        {session.discoveryRationale && (
          <p style={{ marginTop: 18, fontSize: 16, lineHeight: 1.6, color: C.warmGray, maxWidth: 620 }}>
            {session.discoveryRationale}
          </p>
        )}
      </section>

      {primary && (
        <section style={{ padding: "16px 22px 0", maxWidth: 900, margin: "0 auto" }}>
          <PrimaryCard
            rec={primary}
            reconcile={reconcile}
            isChosen={chosen.has(primary.slug)}
            onToggle={() => toggle(primary)}
          />
        </section>
      )}

      {alternatives.length > 0 && (
        <section style={{ padding: "48px 22px 0", maxWidth: 900, margin: "0 auto" }}>
          <Eyebrow color={C.warmGray}>OTHER PROGRAMS WORTH CONSIDERING</Eyebrow>
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            {alternatives.map((rec) => (
              <AlternativeCard
                key={rec.slug}
                rec={rec}
                reconcile={reconcile}
                isChosen={chosen.has(rec.slug)}
                onToggle={() => toggle(rec)}
              />
            ))}
          </div>
        </section>
      )}

      <section style={{ padding: "48px 22px 0", maxWidth: 900, margin: "0 auto" }}>
        <OpenConsultationCard noRecommendations={noRecommendations} />
      </section>

      <SoftLeavePrompt sessionId={sessionId} />

      {totalSelected > 0 && (
        <StickyBottomBar
          totalSelected={totalSelected}
          reviewFee={reviewFee}
          continuing={continuing}
          onContinue={onContinue}
        />
      )}
    </div>
  );
}

/* ----- helpers ----- */

// Be defensive: backends across versions might surface recommendations as
// `discoveryRecommendations`, `recommendations`, or in a sub-field of the
// session. Normalize into a stable shape: { slug, name, tagline, color,
// monthlyPrice, programId, recommendedTier*, whyThisFits[] }.
function extractRecommendations(session) {
  if (!session) return [];
  const raw =
    session.discoveryRecommendations ||
    session.recommendations ||
    session.discovery?.recommendations ||
    [];
  return raw.map((r) => {
    const slug = r.slug || r.programSlug || r.program?.slug;
    const meta = slug ? metaForSlug(slug) : null;
    return {
      slug,
      name: r.name || r.programName || r.program?.name || meta?.t || slug,
      tagline: r.tagline || r.program?.tagline || meta?.q || "",
      color: r.color || r.program?.color || meta?.color || C.peach,
      monthlyPrice: r.monthlyPrice || r.program?.monthlyPrice || 0,
      programId: r.programId || r.program?._id || r.program?.id,
      recommendedTierId: r.recommendedTierId || r.tierId || null,
      recommendedTierKey: r.recommendedTierKey || r.tierKey || null,
      recommendedTierName: r.recommendedTierName || r.tierName || null,
      whyThisFits: Array.isArray(r.whyThisFits) ? r.whyThisFits : [],
      whyAlsoRelevant: r.whyAlsoRelevant || null,
    };
  }).filter((r) => !!r.slug);
}

function PrimaryCard({ rec, reconcile, isChosen, onToggle }) {
  const scheme = colorSchemeForProgram({ slug: rec.slug, color: rec.color });
  const { inPlan, isActive } = reconcile(rec.slug);
  const reconciledLabel = isActive ? "✓ ALREADY ACTIVE" : inPlan ? "✓ ALREADY IN YOUR PLAN" : null;

  return (
    <article
      data-testid={`primary-rec-${rec.slug}`}
      style={{ background: scheme.background, color: scheme.textPrimary, padding: "32px 28px" }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: scheme.textSecondary }}>
        ★ PRIMARY RECOMMENDATION
      </div>
      <h2 style={{ marginTop: 12, fontSize: "clamp(36px, 6vw, 56px)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1, textTransform: "uppercase", color: scheme.textPrimary }}>
        {(rec.name || "").toUpperCase()}
      </h2>
      {rec.tagline && (
        <p style={{ marginTop: 12, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 22, color: scheme.textSecondary }}>
          "{rec.tagline}"
        </p>
      )}
      {rec.whyThisFits.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: scheme.textSecondary }}>WHY THIS FITS YOU</div>
          <ul style={{ marginTop: 10, paddingLeft: 18, fontSize: 15, color: scheme.textPrimary, lineHeight: 1.65 }}>
            {rec.whyThisFits.map((reason, i) => (<li key={i}>{reason}</li>))}
          </ul>
        </div>
      )}
      {rec.monthlyPrice > 0 && (
        <p style={{ marginTop: 22, fontSize: 13, color: scheme.textSecondary }}>
          From {fmtMoney(rec.monthlyPrice)}/mo · physician review $49
        </p>
      )}
      <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
        {reconciledLabel ? (
          <span data-testid={`primary-rec-reconciled-${rec.slug}`} style={{ padding: "12px 20px", background: C.ink, color: C.cream, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            {reconciledLabel}
          </span>
        ) : (
          <Button
            variant="primary"
            size="lg"
            testId={`primary-rec-toggle-${rec.slug}`}
            onClick={onToggle}
          >
            {isChosen ? "✓ ADDED TO MY PLAN" : "ADD TO MY PLAN →"}
          </Button>
        )}
        <Link to={`/app/programs/${rec.slug}`} data-testid={`primary-rec-learn-${rec.slug}`} style={{ textDecoration: "none" }}>
          <Button variant="ghost" size="lg">LEARN MORE</Button>
        </Link>
      </div>
    </article>
  );
}

function AlternativeCard({ rec, reconcile, isChosen, onToggle }) {
  const scheme = colorSchemeForProgram({ slug: rec.slug, color: rec.color });
  const { inPlan, isActive } = reconcile(rec.slug);
  const reconciledLabel = isActive ? "✓ ALREADY ACTIVE" : inPlan ? "✓ ALREADY IN YOUR PLAN" : null;
  return (
    <article
      data-testid={`alt-rec-${rec.slug}`}
      style={{ background: scheme.background, color: scheme.textPrimary, padding: "20px 22px", display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", textTransform: "uppercase", color: scheme.textPrimary }}>
          {rec.name}
        </div>
        {rec.tagline && (
          <div style={{ marginTop: 4, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 15, color: scheme.textSecondary }}>
            "{rec.tagline}"
          </div>
        )}
        {rec.whyAlsoRelevant && (
          <div style={{ marginTop: 8, fontSize: 13, color: scheme.textSecondary, lineHeight: 1.5 }}>
            <strong style={{ fontWeight: 800 }}>Why also relevant:</strong> {rec.whyAlsoRelevant}
          </div>
        )}
      </div>
      {reconciledLabel ? (
        <span data-testid={`alt-rec-reconciled-${rec.slug}`} style={{ padding: "8px 14px", background: C.ink, color: C.cream, fontSize: 10, fontWeight: 800, letterSpacing: "0.18em" }}>
          {reconciledLabel}
        </span>
      ) : (
        <button
          type="button"
          data-testid={`alt-rec-toggle-${rec.slug}`}
          onClick={onToggle}
          style={{
            padding: "10px 18px",
            background: isChosen ? C.ink : "transparent",
            color: isChosen ? C.cream : C.ink,
            border: `1.5px solid ${C.ink}`,
            fontSize: 11, fontWeight: 800, letterSpacing: "0.18em",
            cursor: "pointer", flexShrink: 0,
          }}
        >
          {isChosen ? "✓ ADDED" : "+ ADD"}
        </button>
      )}
    </article>
  );
}

function OpenConsultationCard({ noRecommendations }) {
  return (
    <article data-testid="open-consultation-card" style={{ background: C.sage, color: C.sageDark, padding: "24px 22px" }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.sageDark }}>
        ★ {noRecommendations ? "LET'S TALK" : "NOT SURE ANY OF THESE FIT?"}
      </div>
      <p style={{ marginTop: 12, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 18, lineHeight: 1.5, color: C.ink }}>
        Sometimes a personalized conversation surfaces the right path. Schedule a physician consultation to discuss your goals — they may recommend a program, suggest something we don't currently offer, or refer you to better-suited care.
      </p>
      <div style={{ marginTop: 16 }}>
        <Link to="/app/care-team" data-testid="open-consultation-cta" style={{ textDecoration: "none" }}>
          <Button variant="primary">REQUEST OPEN CONSULTATION — $49</Button>
        </Link>
      </div>
    </article>
  );
}

function SoftLeavePrompt() {
  // Visual reassurance only — modal-on-leave is a future polish per spec §7.
  return (
    <p
      data-testid="discovery-leave-reassurance"
      style={{
        marginTop: 32,
        padding: "0 22px",
        maxWidth: 900,
        marginLeft: "auto",
        marginRight: "auto",
        fontSize: 13,
        color: C.warmGray,
        textAlign: "center",
        fontFamily: '"Instrument Serif", Georgia, serif',
        fontStyle: "italic",
      }}
    >
      We saved your responses. Come back to these recommendations any time within 30 days.
    </p>
  );
}

function StickyBottomBar({ totalSelected, reviewFee, continuing, onContinue }) {
  return (
    <div
      data-testid="discovery-sticky-bar"
      style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40,
        background: C.ink, color: C.cream,
        padding: "16px 22px", borderTop: `3px solid ${C.peach}`,
      }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peach }}>
            ★ {totalSelected} PROGRAM{totalSelected === 1 ? "" : "S"} SELECTED
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: "rgba(250,246,240,0.7)" }}>
            review fee <strong style={{ color: C.cream }}>{fmtMoney(reviewFee)}</strong>
          </div>
        </div>
        <Button variant="peach" testId="discovery-continue-btn" onClick={onContinue} disabled={continuing}>
          {continuing ? "SAVING…" : "CONTINUE TO PAYMENT →"}
        </Button>
      </div>
    </div>
  );
}

function ErrorScreen({ onHome }) {
  return (
    <div style={{ minHeight: "100vh", background: C.cream, padding: 64, textAlign: "center" }}>
      <Eyebrow color={C.coral}>★ HMM</Eyebrow>
      <Headline before="WE COULDN'T" italic="load that." size={48} className="mt-4" />
      <div style={{ marginTop: 32 }}>
        <Button variant="primary" onClick={onHome} testId="discovery-error-home-btn">BACK HOME →</Button>
      </div>
    </div>
  );
}
